import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FloorChips } from 'src/components/FloorChips/FloorChips';
import { FloorMap } from 'src/components/FloorMap/FloorMap';
import { useFloorMap } from 'src/components/FloorMap/FloorMapProvider';
import { RenderBookingObjectParams } from 'src/components/FloorMap/types';
import { fullscreenIsSupported } from 'src/consts';
import { getAgentState, getIsDesktop } from 'src/store/agent/selectors';
import { getFilteredRooms, getRoomsState } from 'src/store/rooms/selectors';
import { openRoom } from 'src/store/rooms/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { BookingObject, PlaceType, Room } from 'src/types';

import { useFloorChips } from '../../components/FloorChips/FloorChipsContext';

import { BookingRoomPlace } from './BookingRoomPlace/BookingRoomPlace';
import { DeskPlace } from './DeskPlace/DeskPlace';

export const renderBookingObject = ({
  bookingObject,
}: RenderBookingObjectParams<BookingObject>): ReactNode => {
  if (bookingObject.type === PlaceType.Room)
    return (
      <BookingRoomPlace key={bookingObject.mapPlaceId} item={bookingObject} />
    );

  if (bookingObject.type === PlaceType.Desk)
    return <DeskPlace key={bookingObject.mapPlaceId} item={bookingObject} />;

  return null;
};

export const RoomBookingFloorMap: FC = () => {
  const { touch } = useSelector(getAgentState);
  const isDesktop = useSelector(getIsDesktop);
  const dispatch = useAppDispatch();
  const { scrollTo } = useFloorMap();
  const { locationLoading, loading, staticObjects, bookingObjects } =
    useSelector(getRoomsState);
  const filteredBookingObjects = useSelector(getFilteredRooms);

  const geoObjects = useMemo((): Map<string, BookingObject> => {
    return new Map([...bookingObjects, ...staticObjects]);
  }, [bookingObjects, staticObjects]);

  const { activeFloor, setActiveFloor, items } = useFloorChips();

  const chips = useMemo((): ReactNode => {
    return (
      <FloorChips
        activeFloor={activeFloor}
        setActiveFloor={setActiveFloor}
        floors={items}
        testId='roomBookingFloors'
      />
    );
  }, [activeFloor, setActiveFloor, items]);

  const book = useCallback(
    async (id: string) => {
      const nextRoom = filteredBookingObjects.get(id) as Room | undefined;

      if (!nextRoom) return;

      dispatch(openRoom(id));

      if (isDesktop) scrollTo(nextRoom);
    },
    [dispatch, filteredBookingObjects, isDesktop, scrollTo],
  );

  return (
    <FloorMap
      loading={loading || locationLoading}
      bookingObjects={geoObjects}
      onClickOnBookingObject={book}
      footer={isDesktop ? chips : null}
      renderBookingObject={renderBookingObject}
      touchDevice={touch}
      fullscreenIsSupported={fullscreenIsSupported && isDesktop}
    />
  );
};
